<template>
  <Multiselect
    v-model="selectedPlace"
    :class="{
      'multiselect--no-options': !places.length
    }"
    label="description"
    track-by="description"
    :placeholder="i18n.placeholder"
    :options="places"
    :loading="isLoading"
    :disabled="false"
    :multiple="false"
    :searchable="true"
    :internal-search="false"
    :clear-on-select="true"
    :show-no-results="true"
    :show-no-options="false"
    :hide-selected="false"
    :allow-empty="true"
    :close-on-select="true"
    v-bind="multiselectWorkaround"
    @search-change="onChange"
    @remove="onRemove"
  >
    <template
      slot="singleLabel"
      slot-scope="props"
    >
      {{ props.option.structured_formatting.main_text }}
    </template>

    <template
      slot="option"
      slot-scope="props"
    >
      {{ props.option.structured_formatting.main_text }}
    </template>

    <template slot="noResult">
      <span class="multiselect__noresult">
        {{ i18n.noResults }}
      </span>
    </template>
  </Multiselect>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import Multiselect from 'vue-multiselect';
import googleMapsLoader from '../js/modules/google-maps-loader';

const TYPES_MAP = {
  REGION: 'administrative_area_level_1',
  CITY: 'locality',
  COUNTRY: 'country',
};

export default {
  name: 'CitySelectGoogle',

  $google: null,
  $autocompleteService: null,
  $geocoder: null,

  components: {
    Multiselect,
  },

  props: {
    i18n: {
      type: Object,
      required: true,
      validator(data) {
        const requiredProps = [
          'placeholder',
          'noResults',
        ];

        return Object.keys(data)
          .filter((key) => includes(requiredProps, key)).length === requiredProps.length;
      },
    },
  },

  data: () => ({
    selectedPlace: [],
    places: [],
    isLoading: false,
    error: false,
  }),

  computed: {
    // https://github.com/shentao/vue-multiselect/issues/685
    multiselectWorkaround() {
      return {
        tabIndex: 0,
      };
    },
  },

  watch: {
    async selectedPlace(selectedPlace) {
      if (isEmpty(selectedPlace)) {
        return;
      }

      const place = await this.getPlaceById(selectedPlace.place_id);

      const getAddressComponentByType = (type) => {
        try {
          return place.address_components
            .find(({ types }) => types[0] === type).short_name.toLowerCase();
        } catch (e) {
          return null;
        }
      };

      // TODO: Format the return value
      this.$emit('input', {
        name: selectedPlace.structured_formatting.main_text,
        city: getAddressComponentByType(TYPES_MAP.CITY),
        region: getAddressComponentByType(TYPES_MAP.REGION),
        country: getAddressComponentByType(TYPES_MAP.COUNTRY),
        id: place.place_id,
      });
    },
  },

  async beforeCreate() {
    const google = await googleMapsLoader.load();

    this.$google = google;
    this.$autocompleteService = new google.maps.places.AutocompleteService();
    this.$geocoder = new google.maps.Geocoder();
  },

  methods: {
    async onChange(input) {
      if (!input) {
        return;
      }

      this.isLoading = true;
      try {
        this.places = await this.getPlacePredictions(input);
      } catch (e) {
        this.error = true;
      }
      this.isLoading = false;
    },

    onRemove() {
      this.$emit('input', {
        id: null,
      });
    },

    getPlacePredictions(input) {
      return new Promise((resolve, reject) => {
        this.$autocompleteService.getPlacePredictions({
          input,
          types: ['(regions)'],
          componentRestrictions: {
            country: 'pl',
          },
        }, (results, status) => {
          const { ZERO_RESULTS, OK } = this.$google.maps.places.PlacesServiceStatus;

          if (status !== OK) {
            reject(new Error(`Unable to get place predictions. Status: ${status}`));
            return;
          }

          if (status === ZERO_RESULTS) {
            resolve([]);
            return;
          }

          const validTypes = Object.values(TYPES_MAP);
          resolve(results.filter(({ types }) => validTypes.includes(types[0])));
        });
      });
    },

    getPlaceById(placeId) {
      return new Promise((resolve, reject) => {
        this.$geocoder.geocode({ placeId }, (results, status) => {
          if (status !== this.$google.maps.GeocoderStatus.OK) {
            reject(new Error(`Unable to get place by ID. Status: ${status}`));
            return;
          }

          resolve(results[0]);
        });
      });
    },
  },
};
</script>
